import { useState, useEffect, useRef } from "react"
import { useQueryState } from "nuqs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faSearch,
  faTimes,
  faDatabase,
  faTriangleExclamation,
  faChevronDown,
  faUsers,
  faUserSlash,
} from "@fortawesome/free-solid-svg-icons"
import { DateTime } from "luxon"
import Dropdown from "../../components/Dropdown"
import ApiError from "../../components/ApiError"
import Loader from "../../components/Loader"
import {
  useVideoErrorStats,
  useVideoScanFilterOptions,
} from "../../hooks/useVideoLogs"

const ALL = "All"

const CATEGORY_OPTIONS = [
  { key: "no_record", label: "No Referring Physician found" },
  { key: "multiple", label: "Multiple Referring Physicians found" },
]

const TODAY = DateTime.now().toISODate()
const DEFAULT_FROM = DateTime.now().minus({ days: 6 }).toISODate()

const dateInputClass =
  "rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"

const formatCreatedDate = (value) => {
  if (!value) return "—"
  const dt =
    value instanceof Date
      ? DateTime.fromJSDate(value)
      : DateTime.fromISO(String(value))
  return dt.isValid ? dt.toFormat("MMM d, yyyy HH:mm") : "—"
}

const tileAccents = {
  blue: "bg-blue-50 text-blue-600",
  red: "bg-red-50 text-red-600",
  violet: "bg-violet-50 text-violet-600",
  amber: "bg-amber-50 text-amber-600",
  teal: "bg-teal-50 text-teal-600",
  sky: "bg-sky-50 text-sky-600",
  rose: "bg-rose-50 text-rose-600",
  gray: "bg-gray-100 text-gray-600",
}

const StatTile = ({
  label,
  value,
  valueSuffix,
  hint,
  icon,
  accent = "blue",
  sub,
  onClick,
  active,
}) => {
  const inner = (
    <>
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tileAccents[accent]}`}
      >
        <FontAwesomeIcon className="w-4 h-4" icon={icon} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-bold text-gray-900 leading-none tabular-nums">
          {(value ?? 0).toLocaleString()}
          {valueSuffix && (
            <span className="ml-2 text-sm font-bold text-gray-500">
              {valueSuffix}
            </span>
          )}
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mt-1">
          {label}
        </div>
        {sub && (
          <div className="text-sm font-bold text-gray-600 mt-0.5">{sub}</div>
        )}
      </div>
    </>
  )
  const base =
    "flex items-center gap-3 bg-white rounded-xl px-4 py-3 flex-1 min-w-[190px] shadow-sm text-left"

  if (onClick) {
    return (
      <button
        className={`${base} border transition-colors ${active ? "border-blue-500 ring-1 ring-blue-500" : "border-gray-200 hover:border-gray-300"}`}
        title={hint}
        type="button"
        onClick={onClick}
      >
        {inner}
      </button>
    )
  }
  return (
    <div className={`${base} border border-gray-200`} title={hint}>
      {inner}
    </div>
  )
}

const Cell = ({ children, className = "" }) => (
  <td className={`px-4 py-2.5 text-sm text-gray-700 align-top ${className}`}>
    {children}
  </td>
)

const AccessionList = ({ accessions }) => {
  const [expanded, setExpanded] = useState(false)

  if (!accessions?.length) return <span className="text-gray-400">—</span>

  const count = accessions.length

  return (
    <div className="min-w-[150px]">
      <button
        className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-colors"
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span className="tabular-nums font-semibold">{count}</span>
        accession{count === 1 ? "" : "s"}
        <FontAwesomeIcon
          className={`w-2.5 h-2.5 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`}
          icon={faChevronDown}
        />
      </button>
      {expanded && (
        <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white p-2">
          <div className="flex flex-col gap-1">
            {accessions.map((accession) => (
              <span
                key={accession}
                className="font-mono text-[11px] text-gray-600"
              >
                {accession}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const INFORMATIVE_DETAIL = /matches on name|unusable|no record after|city=/i

const DETAIL_HIGHLIGHT = /(\d+)(?=\s+(?:NPPES\s+)?match)/gi

const DetailHighlight = ({ text }) => {
  if (!text) return <span className="text-gray-400">—</span>
  const nodes = []
  let last = 0
  for (const m of text.matchAll(DETAIL_HIGHLIGHT)) {
    if (m.index > last) nodes.push(text.slice(last, m.index))
    nodes.push(
      <span
        key={`c${m.index}`}
        className="font-semibold text-violet-700 bg-violet-50 rounded px-1 tabular-nums"
      >
        {m[1]}
      </span>
    )
    last = m.index + m[0].length
  }
  if (last < text.length) nodes.push(text.slice(last))
  return <>{nodes}</>
}

const columns = [
  { header: "First name" },
  { header: "Last name" },
  { header: "City" },
  { header: "State" },
  { header: "Error Details" },
  { header: "Created date" },
  { header: "Accession numbers" },
]

const VideoErrorStats = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [activeSearchTerm, setActiveSearchTerm] = useQueryState("search", {
    defaultValue: "",
  })
  const [from, setFrom] = useQueryState("from", { defaultValue: DEFAULT_FROM })
  const [to, setTo] = useQueryState("to", { defaultValue: TODAY })
  const [issuer, setIssuer] = useQueryState("issuer", { defaultValue: ALL })
  const [category, setCategory] = useQueryState("category", {
    defaultValue: "no_record",
  })

  const sentinelRef = useRef(null)

  const { data: filterOptions } = useVideoScanFilterOptions()

  const getDateFilterValue = () => {
    if (!from && !to) return undefined
    return [from || "", to || ""].join(",")
  }

  const isMultiple = category === "multiple"

  const filters = {
    searchTerm: activeSearchTerm?.trim() || undefined,
    timePeriod: getDateFilterValue(),
    patientIssuer: issuer === ALL ? undefined : issuer,
    category,
  }

  const {
    data: { summary, groups, total } = {
      summary: {
        totalRecords: 0,
        failedRecords: 0,
        noRecordRecords: 0,
        uniqueNotFound: 0,
        multipleWith: 0,
        multipleWithout: 0,
      },
      groups: [],
      total: 0,
    },
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useVideoErrorStats({ limit: 20, filters })

  const selectedCategory =
    CATEGORY_OPTIONS.find((c) => c.key === category) || CATEGORY_OPTIONS[0]
  const multipleTotal = summary.multipleWith + summary.multipleWithout

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: "200px", threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    setSearchTerm(activeSearchTerm || "")
  }, [activeSearchTerm])

  const handleClearFilters = () => {
    setFrom(DEFAULT_FROM)
    setTo(TODAY)
    setIssuer(ALL)
  }

  const handleClearSearch = () => {
    setSearchTerm("")
    setActiveSearchTerm("")
  }

  const failedRate = summary.totalRecords
    ? Math.round((summary.failedRecords / summary.totalRecords) * 100)
    : 0

  const hasActiveFilters =
    from !== DEFAULT_FROM || to !== TODAY || issuer !== ALL

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">
          Video Matching Insights
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Health of the report-to-video pipeline: how often matching failed and
          which referring physicians could not be resolved.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label className="text-xs font-medium mb-1 ml-1 text-gray-700">
              DATE RANGE
            </label>
            <div className="flex items-center gap-2">
              <input
                className={dateInputClass}
                max={to || TODAY}
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
              <span className="text-gray-400">–</span>
              <input
                className={dateInputClass}
                max={TODAY}
                min={from || undefined}
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
          </div>

          <Dropdown
            label="ISSUER"
            options={[ALL, ...(filterOptions?.patientIssuers || [])]}
            setValue={setIssuer}
            title="Filter by issuer"
            value={issuer}
          />

          <Dropdown
            label="CATEGORY"
            options={CATEGORY_OPTIONS.map((c) => c.label)}
            setValue={(label) =>
              setCategory(
                CATEGORY_OPTIONS.find((c) => c.label === label)?.key ||
                  "no_record"
              )
            }
            title="Filter the table by error category"
            value={selectedCategory.label}
          />

          <button
            className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!hasActiveFilters}
            title="Reset filters to their defaults"
            onClick={handleClearFilters}
          >
            Reset
          </button>

          <div className="flex-1" />

          <div>
            <label className="block text-xs font-medium mb-1 ml-1 text-gray-700">
              ACCESSION / PHYSICIAN
            </label>
            <div className="relative">
              <FontAwesomeIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
                icon={faSearch}
              />
              <input
                className="w-full min-w-[300px] pl-9 pr-9 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search and press Enter"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    setActiveSearchTerm(searchTerm)
                  }
                }}
              />
              {searchTerm && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  title="Clear search"
                  type="button"
                  onClick={handleClearSearch}
                >
                  <FontAwesomeIcon className="w-3 h-3" icon={faTimes} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-3">
        <StatTile
          accent="blue"
          hint="Total records logged in this period"
          icon={faDatabase}
          label="Total Records"
          value={summary.totalRecords}
        />
        <StatTile
          accent="red"
          hint={`Errored records — ${failedRate}% of total`}
          icon={faTriangleExclamation}
          label="Failed Records"
          sub={`${failedRate}% of total`}
          value={summary.failedRecords}
        />
        <StatTile
          accent="amber"
          hint="Errored records with no NPPES record found for the referring physician"
          icon={faUserSlash}
          label="No Referring Physician found"
          value={summary.noRecordRecords}
          valueSuffix={`(${summary.uniqueNotFound.toLocaleString()} unique physicians)`}
        />
        <StatTile
          accent="violet"
          hint="Records with 2 or more NPPES matches for the referring physician"
          icon={faUsers}
          label="Multiple Referring Physicians found"
          value={multipleTotal}
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20 gap-4">
          <Loader />
          <span className="text-gray-600">Loading error stats...</span>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-2 px-1">
            <h2 className="text-sm font-semibold text-gray-700">
              {selectedCategory.label}
              <span className="ml-2 font-normal text-xs text-gray-500">
                {total.toLocaleString()} referring physician
                {total === 1 ? "" : "s"}
              </span>
            </h2>
            {activeSearchTerm && (
              <span className="text-xs text-gray-500">
                Filtered by{" "}
                <span className="font-semibold text-gray-700">
                  {activeSearchTerm}
                </span>
              </span>
            )}
          </div>

          {groups.length > 0 ? (
            <div className="border border-gray-200 rounded-xl overflow-auto max-h-[calc(100vh-430px)] bg-white shadow-sm">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    {columns.map(({ header, align = "text-left" }) => (
                      <th
                        key={header}
                        className={`px-4 py-2.5 ${align} text-[10px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap border-b border-gray-200`}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {groups.map((group, index) => {
                    const detail =
                      isMultiple || INFORMATIVE_DETAIL.test(group.message || "")
                        ? group.message
                        : "No Referring Physician found"
                    return (
                      <tr
                        key={`${group.refName || "unknown"}-${index}`}
                        className="hover:bg-blue-50/40 transition-colors"
                      >
                        <Cell className="whitespace-nowrap font-medium text-gray-900">
                          {group.firstName || (
                            <span className="text-gray-400 font-normal">—</span>
                          )}
                        </Cell>
                        <Cell className="whitespace-nowrap font-medium text-gray-900">
                          {group.lastName || (
                            <span className="text-gray-400 font-normal">—</span>
                          )}
                        </Cell>
                        <Cell className="whitespace-nowrap text-gray-600">
                          {group.city || (
                            <span className="text-gray-400">—</span>
                          )}
                        </Cell>
                        <Cell className="whitespace-nowrap text-gray-600 tabular-nums">
                          {group.state || (
                            <span className="text-gray-400">—</span>
                          )}
                        </Cell>
                        <Cell className="text-gray-600 max-w-[380px]">
                          <DetailHighlight text={detail} />
                        </Cell>
                        <Cell className="whitespace-nowrap text-xs text-gray-500">
                          {formatCreatedDate(group.createdDate)}
                        </Cell>
                        <Cell>
                          <AccessionList accessions={group.accessions} />
                        </Cell>
                      </tr>
                    )
                  })}
                </tbody>
              </table>

              <div ref={sentinelRef} className="h-1" />

              {hasNextPage && (
                <div className="flex justify-center py-4 border-t border-gray-100">
                  {isFetchingNextPage ? (
                    <div className="flex items-center gap-2">
                      <Loader />
                      <span className="text-gray-600">
                        Loading more records...
                      </span>
                    </div>
                  ) : (
                    <button
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors text-sm font-medium"
                      onClick={fetchNextPage}
                    >
                      Load More
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-16 bg-white border border-gray-200 rounded-xl">
              <FontAwesomeIcon
                className="w-8 h-8 text-gray-300"
                icon={faTriangleExclamation}
              />
              <p className="text-sm text-gray-500">
                No records for this category and these filters.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default VideoErrorStats
